import type {
  EmulatorAdapter,
  EmulatorCapabilities,
  EmulatorLaunchRequest,
  EmulatorOperation,
  EmulatorRuntimeFailureReason,
  EmulatorRuntimeListener,
  EmulatorRuntimeSnapshot,
} from './contracts';
import {
  buildEmulatorJsDefaultControllers,
  defaultGbaInputProfile,
  type EmulatorJsDefaultControllers,
} from './input-profile';
import { emulatorJsGbaRuntime, type EmulatorRuntimeManifest } from './runtime-manifest';
import { EmulatorRuntimeSession } from './runtime-session';
import { assertOperationAllowed, EmulatorContractError } from './runtime-state';

export interface EmulatorJsHostLaunchConfiguration {
  readonly loaderUrl: string;
  readonly dataBaseUrl: string;
  readonly gameUrl: string;
  readonly gameName: string;
  readonly gameId: string;
  readonly system: 'gba';
  readonly expectedCore: 'mgba';
  readonly controlScheme: 'gba';
  readonly defaultControllers: EmulatorJsDefaultControllers;
  readonly threads: false;
}

export class EmulatorJsRuntimeHostError extends Error {
  readonly reason: EmulatorRuntimeFailureReason;

  constructor(reason: EmulatorRuntimeFailureReason, message: string) {
    super(message);
    this.name = 'EmulatorJsRuntimeHostError';
    this.reason = reason;
  }
}

export interface EmulatorJsRuntimeHost {
  launch(configuration: EmulatorJsHostLaunchConfiguration): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  restart(): Promise<void>;
  setVolume(volume: number): Promise<void>;
  requestFullscreen(): Promise<void>;
  exportNativeSave(): Promise<Uint8Array | null>;
  importNativeSave(data: Uint8Array): Promise<void>;
  captureSaveState(): Promise<Uint8Array>;
  restoreSaveState(data: Uint8Array): Promise<void>;
  setCheat(index: number, enabled: boolean, code: string): Promise<void>;
  resetCheats(): Promise<void>;
  destroy(): void;
}

const runtimeCapabilities: EmulatorCapabilities = {
  pause: true,
  restart: true,
  volume: true,
  fullscreen: true,
  keyboard: true,
  gamepad: true,
  nativeSave: true,
  saveStates: true,
  cheats: true,
};

function runtimeFailureReason(error: unknown): EmulatorRuntimeFailureReason | undefined {
  return error instanceof EmulatorJsRuntimeHostError ? error.reason : undefined;
}

export class EmulatorJsAdapter implements EmulatorAdapter {
  readonly adapterId = 'emulatorjs-iframe-v1';
  readonly runtime: EmulatorRuntimeManifest;

  private readonly host: EmulatorJsRuntimeHost;
  private readonly session: EmulatorRuntimeSession;

  constructor(host: EmulatorJsRuntimeHost, runtime = emulatorJsGbaRuntime) {
    this.host = host;
    this.runtime = runtime;
    this.session = new EmulatorRuntimeSession(runtime);
  }

  capabilities(): EmulatorCapabilities {
    return runtimeCapabilities;
  }

  snapshot(): EmulatorRuntimeSnapshot {
    return this.session.snapshot();
  }

  subscribe(listener: EmulatorRuntimeListener): () => void {
    return this.session.subscribe(listener);
  }

  async launch(request: EmulatorLaunchRequest): Promise<EmulatorRuntimeSnapshot> {
    const loading = this.session.beginLaunch(request);
    const identity = loading.identity;

    if (!identity) {
      throw new EmulatorContractError(
        'INVALID_LAUNCH_REQUEST',
        'Runtime identity was not created for the launch request.',
        false,
        { operation: 'launch', state: loading.state, runtimeId: this.runtime.runtimeId },
      );
    }

    this.host.destroy();

    try {
      await this.host.launch({
        loaderUrl: this.runtime.release.loaderUrl,
        dataBaseUrl: this.runtime.release.dataBaseUrl,
        gameUrl: request.romUrl,
        gameName: request.displayName,
        gameId: identity.gameKey,
        system: 'gba',
        expectedCore: this.runtime.core.emulatorJsCore,
        controlScheme: 'gba',
        defaultControllers: buildEmulatorJsDefaultControllers(
          request.inputProfile ?? defaultGbaInputProfile,
        ),
        threads: false,
      });
      return this.session.markReady();
    } catch (error) {
      this.host.destroy();
      return this.session.fail(
        'RUNTIME_LOAD_FAILED',
        'EmulatorJS failed to start the pinned GBA runtime.',
        true,
        runtimeFailureReason(error),
      );
    }
  }

  async pause(): Promise<EmulatorRuntimeSnapshot> {
    return this.executeControl(
      'pause',
      () => this.host.pause(),
      () => this.session.markPaused(),
    );
  }

  async resume(): Promise<EmulatorRuntimeSnapshot> {
    return this.executeControl(
      'resume',
      () => this.host.resume(),
      () => this.session.markResumed(),
    );
  }

  async restart(): Promise<EmulatorRuntimeSnapshot> {
    return this.executeControl(
      'restart',
      () => this.host.restart(),
      () => this.session.markRestarted(),
    );
  }

  async setVolume(volume: number): Promise<EmulatorRuntimeSnapshot> {
    if (!Number.isFinite(volume) || volume < 0 || volume > 1) {
      throw new RangeError('Volume must be a finite number between 0 and 1.');
    }

    return this.executeControl(
      'set-volume',
      () => this.host.setVolume(volume),
      () => this.session.setVolume(volume),
    );
  }

  async requestFullscreen(): Promise<EmulatorRuntimeSnapshot> {
    return this.executeControl(
      'fullscreen',
      () => this.host.requestFullscreen(),
      () => this.session.snapshot(),
    );
  }

  async exportNativeSave(): Promise<Uint8Array | null> {
    return this.executeBinaryControl('export-native-save', () => this.host.exportNativeSave());
  }

  async importNativeSave(data: Uint8Array): Promise<EmulatorRuntimeSnapshot> {
    this.validatePersistencePayload(data);
    return this.executeControl(
      'import-native-save',
      () => this.host.importNativeSave(data),
      () => this.session.snapshot(),
    );
  }

  async captureSaveState(): Promise<Uint8Array> {
    const state = await this.executeBinaryControl(
      'capture-save-state',
      () => this.host.captureSaveState(),
      true,
    );
    if (!state) {
      throw new EmulatorContractError(
        'RUNTIME_EXECUTION_FAILED',
        'EmulatorJS returned no save-state payload.',
        true,
        { operation: 'capture-save-state', state: this.session.snapshot().state },
      );
    }
    return state;
  }

  async restoreSaveState(data: Uint8Array): Promise<EmulatorRuntimeSnapshot> {
    this.validatePersistencePayload(data);
    return this.executeControl(
      'restore-save-state',
      () => this.host.restoreSaveState(data),
      () => this.session.snapshot(),
    );
  }

  async setCheat(index: number, enabled: boolean, code: string): Promise<EmulatorRuntimeSnapshot> {
    if (!Number.isInteger(index) || index < 0 || index >= 64) {
      throw new RangeError('Cheat index must be an integer between 0 and 63.');
    }
    const normalizedCode = code.trim();
    if (!normalizedCode || normalizedCode.length > 4096) {
      throw new RangeError('Cheat code must be between 1 and 4096 characters.');
    }

    return this.executeControl(
      'set-cheat',
      () => this.host.setCheat(index, enabled, normalizedCode),
      () => this.session.snapshot(),
    );
  }

  async resetCheats(): Promise<EmulatorRuntimeSnapshot> {
    return this.executeControl(
      'reset-cheats',
      () => this.host.resetCheats(),
      () => this.session.snapshot(),
    );
  }

  async terminate(): Promise<EmulatorRuntimeSnapshot> {
    const snapshot = this.session.terminate();
    this.host.destroy();
    return snapshot;
  }

  dispose(): void {
    this.host.destroy();
  }

  private validatePersistencePayload(data: Uint8Array): void {
    if (!(data instanceof Uint8Array) || data.byteLength === 0) {
      throw new EmulatorContractError(
        'INVALID_PERSISTENCE_PAYLOAD',
        'Persistence payload must be a non-empty Uint8Array.',
        true,
        { state: this.session.snapshot().state, runtimeId: this.runtime.runtimeId },
      );
    }
  }

  private async executeBinaryControl(
    operation: Extract<EmulatorOperation, 'export-native-save' | 'capture-save-state'>,
    invoke: () => Promise<Uint8Array | null>,
    required = false,
  ): Promise<Uint8Array | null> {
    const before = this.session.snapshot();
    assertOperationAllowed(operation, before.state);

    try {
      const data = await invoke();
      if (required && (!data || data.byteLength === 0)) {
        throw new EmulatorJsRuntimeHostError(
          'PERSISTENCE_PAYLOAD_INVALID',
          'Runtime returned an empty persistence payload.',
        );
      }
      return data;
    } catch (error) {
      const reason = runtimeFailureReason(error);
      throw new EmulatorContractError(
        'RUNTIME_EXECUTION_FAILED',
        `EmulatorJS failed to apply runtime operation ${operation}.`,
        true,
        {
          operation,
          state: before.state,
          runtimeId: this.runtime.runtimeId,
          ...(before.identity
            ? { gameId: before.identity.gameId, variantId: before.identity.variantId }
            : {}),
          ...(reason ? { reason } : {}),
        },
      );
    }
  }

  private async executeControl(
    operation: EmulatorOperation,
    invoke: () => Promise<void>,
    commit: () => EmulatorRuntimeSnapshot,
  ): Promise<EmulatorRuntimeSnapshot> {
    const before = this.session.snapshot();
    assertOperationAllowed(operation, before.state);

    try {
      await invoke();
      return commit();
    } catch (error) {
      const reason = runtimeFailureReason(error);
      throw new EmulatorContractError(
        'RUNTIME_EXECUTION_FAILED',
        `EmulatorJS failed to apply runtime operation ${operation}.`,
        true,
        {
          operation,
          state: before.state,
          runtimeId: this.runtime.runtimeId,
          ...(before.identity
            ? { gameId: before.identity.gameId, variantId: before.identity.variantId }
            : {}),
          ...(reason ? { reason } : {}),
        },
      );
    }
  }
}
